/**
 * @file   carwash_website\Carwash-FrontEnd\src\Pages\CWOAddNewUser.tsx
 * @brief  Add/Edit CWO user page.
 * @date   SEP, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { React, Container, Button, Row, Col, Form, useOutletContext, useNavigate, useEffect, useState, useRef, useParams } from '../utils/thirdpartyComponents';
import CWSelect from '../components/CWSelect';
import CWInput from '../components/CWInput';
import CWPasswordInput from '../components/CWPasswordInput';
import CWCheckbox from '../components/CWCheckbox';
import { AppContextType } from '../interfaces/generalInterface';
import { pageURLs } from '../utils/pageUrls';
import { MessageToaster } from '../utils/toastUtil';
import { LocationItem, Option } from '../interfaces/subscriptionInterface';
import { LocationApi,LocationManagerApi } from '../utils/apiUrls';
import axios from '../config/axiosConfig';
import { cwoAddnewUserSchema, editcwoAddnewUserSchema } from '../validations/cwoAddnewUserSchema';
import FullLoader from '../components/FullLoader';
import * as Yup from 'yup';
import { EMAIL_MAX_LENGTH, NAME_MAX_LENGTH, PHONE_MAX_LENGTH } from '../utils/constants';
import { formatPhoneNumber, formatPhoneNumberforApi } from '../utils/helper';
import { userType } from '../utils/enums';

const CWOAddNewUser = (props: { edit?: boolean | undefined; userType?: string | undefined }) => {
    const { edit = false } = props; // Accept edit prop and user data for edit mode
    const param = useParams();
    const current_user_id= param.id ? param.id : 0;
    const { updateData } = useOutletContext<AppContextType>();
    const [loading, setLoading] = useState(false);
    // Use a ref to store input elements
    const formRef = useRef<HTMLFormElement>(null);
    // State to handle locations given in dropdown
    const [locations, setLocations] = useState<Option[]>([]);
    // Function to get Locations
    const getLocations = async () => {
        try {

            const { data } = await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS);
            if (data.error === 0) {
                const location_list: LocationItem[] = data?.data

                // Transform the array
                const transformedArray: Option[] = location_list.map(item => ({
                    label: item.locationName,
                    value: item.locationId,
                }));

                setLocations(transformedArray)
            }
            else {
                // show error message
                toaster.toastError(data.message);
            }
        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

        // To get equipment
        const getLocationmanager = async () => {
            const userId = current_user_id;
            if(userId !== 0)
            {
                try {
                    setLoading(true); // Show loader
                    let param = '';
                    param += userId;
                    const { data } = (props.userType !== userType['CARWASH-OWNER']) ? await axios.get(LocationManagerApi.CARWASH_OWNER_GET_LOCATIONMANAGER + param) : await axios.get(LocationManagerApi.CARWASH_OWNER_GET_LOCATIONMANAGER + param)
                    setFormValues({
                        ...formValues,
                        selectlocation: data?.data?.LocationId,
                        name: data?.data?.fullName,
                        emailid: data?.data?.email,
                        phonenumber: data?.data?.phone ? formatPhoneNumber(data?.data?.phone) : '',
                        fullaccess: data?.data?.PermissionType === 1 ? true : false,
                        washmanagement:data?.data?.PermissionType === 2 ? true : false,
                        permissionType: data?.data?.PermissionType,
                        userId: data?.data?.userId,
                    });
                    
                } catch (error: any) {
                    throw new Error(error.response?.data?.message || error.message);
                } finally {
                    setLoading(false); // Hide loader
                }
               
            }
        }


    useEffect(() => {
        getLocations();
        getLocationmanager();
    }, []);

    const toaster = new MessageToaster();
    const navigate = useNavigate();

    const [formValues, setFormValues] = useState(props.edit ?
        {
            selectlocation: 0,
            name: '',
            emailid: '',
            phonenumber: '',
            password1: '',
            password2: '',
            fullaccess: false,
            washmanagement:true,
            permissionType: 2,
            userId: 0,
        } : {
            selectlocation: '',
            name: '',
            emailid: '',
            phonenumber: '',
            password1: '',
            password2: '',
            fullaccess: false,
            washmanagement:true,
            permissionType: 2
        });

    const [formErrors, setFormErrors] = useState({
        selectlocation: '',
        name: '',
        emailid: '',
        phonenumber: '',
        password1: '',
        password2: '',
    });

    useEffect(() => {
        handleUpdate();
    }, [edit]);

    const handleUpdate = () => {
        updateData({
            heading: edit ? 'Edit User' : 'Add New User', // Update heading based on mode
            url: `/${pageURLs.location_managers}`,
            showBackButton: true,
            showMainMenu: true,
            activeLink: 'manage-users',
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(formErrors);
        if (keys.includes(name)) {
            setFormErrors({
                ...formErrors,
                [name]: ""
            })
        }

        if (name === 'phonenumber') {
            const phoneValue = value;
            if (!/^\d*$/.test(phoneValue)) {
                e.target.value = phoneValue.replace(/\D/g, '');
            }
            const formattedValue = formatPhoneNumber(e.target.value);
            setFormValues({
                ...formValues,
                [name]: formattedValue,
            });
            return;
        }

        setFormValues({
            ...formValues,
            [name]: value,
        });
    };

    // Handle the paste event to allow only 10 digits in phone number
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('Text');
        const formattedData = pastedData.replace(/\D/g, '').slice(0, 10);
        const formattedValue = formatPhoneNumber(formattedData);
        setFormValues({
            ...formValues,
            phonenumber: formattedValue,
        });
    };

    // Triggers whenever options is selected
    const locationhandleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption?.value || '';

        // To remove validation when user enters input
        const keys = Object.keys(formErrors);
        if (keys.includes(name)) {
            setFormErrors({
                ...formErrors,
                [name]: ""
            })
        }
        
        const newFormData = {
            ...formValues,
            [name]: value,
        };
        setFormValues(newFormData);
    };

    const setCheckbox = async (e: any) => {
        if(e.target.name === 'washmanagement')
        {
            setFormValues({
                ...formValues,
                washmanagement: true,
                fullaccess: false,
                permissionType: 2,
            });

        }
        else if(e.target.name === 'fullaccess')
        {
            setFormValues({
                ...formValues,
                washmanagement: false,
                fullaccess: true,
                permissionType: 1,
            });

        }
    }

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setLoading(true);
            if (edit) {
                await editcwoAddnewUserSchema.validate(formValues, { abortEarly: false });
                // Handle edit user submission
                const responseLocationManagerUpdate = await axios.post(LocationManagerApi.CARWASH_OWNER_EDIT_LOCATIONMANAGER, {
                    name: formValues.name,
                    permissionType: formValues.permissionType,
                    locationId: formValues.selectlocation,
                    userId: formValues.userId,
                    phoneNumber: formatPhoneNumberforApi(formValues.phonenumber),
                    });
                setLoading(false)
                if (responseLocationManagerUpdate?.data?.error === 0) {
                    setFormErrors({
                        selectlocation: '',
                        name: '',
                        emailid: '',
                        phonenumber: '',
                        password1: '',
                        password2: '',
                    });
                    
                    toaster.toastSuccess(responseLocationManagerUpdate?.data?.message);
                    
                }
                else {
                    toaster.toastError(responseLocationManagerUpdate?.data?.message);
                }
                navigate("/" + pageURLs.location_managers);
            } else {
                await cwoAddnewUserSchema.validate(formValues, { abortEarly: false });
                // Handle add user submission
                const responseLocationManagerAdd = await axios.post(LocationManagerApi.CARWASH_OWNER_CREATE_LOCATIONMANAGER, {
                    name: formValues.name,
                    email: formValues.emailid,
                    password: formValues.password1,
                    repassword: formValues.password2,
                    permissionType: formValues.permissionType,
                    locationId: formValues.selectlocation,
                    phoneNumber: formatPhoneNumberforApi(formValues.phonenumber)
                    });
                setLoading(false)
                if (responseLocationManagerAdd?.data?.error === 0) {
                    setFormErrors({
                        selectlocation: '',
                        name: '',
                        emailid: '',
                        phonenumber: '',
                        password1: '',
                        password2: '',
                    });
                    
                    toaster.toastSuccess(responseLocationManagerAdd?.data?.message);
                    
                }
                else {
                    toaster.toastError(responseLocationManagerAdd?.data?.message);
                }
                navigate("/" + pageURLs.location_managers);
            }
    } catch (error) {

        const newErrors: any = {};

        //If validation fails on submit, then errors are shown
        if (error instanceof Yup.ValidationError) {

            error.inner.forEach((err) => {
                newErrors[err.path as keyof typeof formValues] = err.message;
            });
            setFormErrors(newErrors);
        }

        // Focus on the first invalid field
        if (formRef.current) {
            const firstInvalidField = Object.keys(newErrors)[0];
            const invalidElement = formRef.current.querySelector<HTMLInputElement>(`[name="${firstInvalidField}"]`);
            if (invalidElement) {
                invalidElement.focus();
            }
        }

    } finally {
        setLoading(false)
    }

    };

    return (
        <div className='hardware-listing-container addnew-item-wrap'>
            <Container>
                <div className="hardware-list-wrap">
                    <div className="form-inner mx-auto">
                        <Form onSubmit={handleSubmit} ref={formRef}>
                            <Row>
                                <Col md={12}>
                                        <CWSelect
                                            id="washlocation"
                                            name="washlocation"
                                            options={locations}
                                            values={locations.filter(option => option.value === formValues.selectlocation)}
                                            onChange={(selectedOptions) => locationhandleSelectChange(selectedOptions[0], 'selectlocation')}
                                            label="Car Wash Location"
                                            errorMessage={formErrors.selectlocation && formErrors.selectlocation}
                                            dropdownGap={0}
                                            dropdownPosition="auto"
                                            placeholder="Select Car Wash Location"
                                            multi={false}
                                            isMandatory
                                            className="no-input"
                                        />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="name"
                                        name="name"
                                        type="text"
                                        label="Name"
                                        value={formValues.name}
                                        onChange={handleInputChange}
                                        isMandatory
                                        errorMessage={formErrors.name}
                                        autoComplete="off"
                                        maxLength={NAME_MAX_LENGTH}
                                        placeholder='Enter Name'
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWInput
                                        id="emailid"
                                        name="emailid"
                                        type="text"
                                        label="Email Address"
                                        placeholder='Enter Email Address'
                                        value={formValues.emailid}
                                        onChange={handleInputChange}
                                        isMandatory
                                        errorMessage={formErrors.emailid}
                                        autoComplete="off"
                                        maxLength={EMAIL_MAX_LENGTH}
                                        readOnly={props.edit ? true : false}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <CWInput
                                        id="phonenumber"
                                        name="phonenumber"
                                        type="text"
                                        label="Phone Number"
                                        placeholder='Enter Phone Number'
                                        value={formValues.phonenumber}
                                        onChange={handleInputChange}
                                        onPaste={handlePaste}
                                        isMandatory
                                        errorMessage={formErrors.phonenumber}
                                        autoComplete="off"
                                        maxLength={PHONE_MAX_LENGTH}
                                    />
                                </Col>
                            </Row>

                            {props.edit ? '' : 
                            <Row>
                                <Col md={6}>
                                    <CWPasswordInput
                                        id="password1"
                                        name="password1"
                                        type="password"
                                        label="Password"
                                        placeholder='Enter Password'
                                        value={formValues.password1}
                                        showpass={<span />}
                                        isMandatory
                                        maxLength={PHONE_MAX_LENGTH}
                                        errorMessage={formErrors.password1}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                                <Col md={6}>
                                    <CWPasswordInput
                                        id="password2"
                                        name="password2"
                                        type="password"
                                        label="Re-Type Password"
                                        value={formValues.password2}
                                        showpass={<span />}
                                        isMandatory
                                        maxLength={PHONE_MAX_LENGTH}
                                        errorMessage={formErrors.password2}
                                        onChange={handleInputChange}
                                    />
                                </Col>
                            </Row>
                            }
                            <div className="d-flex align-items-center access-checkgroup">
                                <CWCheckbox
                                    id="washmanagement"
                                    name="washmanagement"
                                    label="Car Wash Management"
                                    checked={formValues.washmanagement}
                                    onChange={(e) => { setCheckbox(e); }}
                                />
                                <CWCheckbox
                                    id="fullaccess"
                                    name="fullaccess"
                                    label="Full Access"
                                    checked={formValues.fullaccess}
                                    onChange={(e) => { setCheckbox(e); }}
                                />
                            </div>
                            <div className="btn-sec d-flex justify-content-end">
                                <Button title="Cancel" variant="secondary" className="secondary-btn" onClick={() => { navigate("/" + pageURLs.location_managers) }}>
                                    Cancel
                                </Button>
                                <Button
                                    title={edit ? "Update" : "Save"} // Dynamic button text
                                    variant="primary"
                                    className="primary-btn"
                                    type="submit"
                                >
                                    {edit ? "Update" : "Save"}
                                </Button>
                            </div>
                        </Form>
                    </div>
                </div>
            </Container>
            {loading && <FullLoader />}
        </div>
    );
};

export default CWOAddNewUser;